import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT, Namespace, useApiErrorMessage } from "@racoon/i18n";
import {
  useCompleteDriverOnShiftMutation,
  useCancelDriverOnShiftMutation,
  useApproveDriverOnShiftMutation,
  useLazyDriverOnShiftExistsQuery,
  useLazyGetDriverOnShiftByIdQuery,
} from "~/shared/api/shift/driver-on-shift.grpc";
import { useToast, useModal, useCompanyBase, Button } from "@racoon/ui";
import { ConnectProto, ErrorCode, type ApiError } from "@racoon/api";
import { fromTimestamp, toDateParam } from "@racoon/lib";
import { isShiftRunning } from "../helpers";
import { useForceCompleteShiftModal } from "~/pages/dashboard/routes/components/force-complete-shift/use-force-complete-shift-modal";

/**
 * A shift id as it travels through the UI. The lock keys on `String(id)`, so a
 * proto bigint and its decimal string are the same shift; `Number` is avoided
 * because it would round an int64 past 2^53 and key the wrong shift.
 */
export type ShiftId = number | bigint | string;

type BusyKind = "approve" | "complete" | "cancel";

/** The facts behind that one failure, read from the code and args, never the message. */
function unfinishedWork(error: unknown): { shiftId: string; routeId: string } | undefined {
  const failure = error as Partial<ApiError> | undefined;
  if (failure?.code !== ErrorCode.ERROR_CODE_DRIVER_ON_SHIFT_ROUTE_UNFINISHED) return undefined;
  const { id, route } = failure.args ?? {};
  return id && route ? { shiftId: id, routeId: route } : undefined;
}

interface ApprovalShiftData {
  id: number | bigint;
  status: ConnectProto.DriverOnShiftStatus;
  truckId?: number | bigint;
  driverId?: number | bigint;
  driverName?: string;
  plateNumber?: string;
  releasedAt?: string | Date;
  startedAt?: { seconds?: string | number | bigint; nanos?: number } | string | Date;
  twStartAt?: { seconds?: string | number | bigint; nanos?: number } | string | Date;
  createdAt?: { seconds?: string | number | bigint; nanos?: number } | string | Date;
}

const toDate = (raw: unknown): Date | null => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "string") {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return fromTimestamp(raw as { seconds?: string | number | bigint; nanos?: number });
};

/**
 * The day the route sheet must open on to hold this shift. That view lists a
 * day at a time and the backend windows it on `started_at`, falling back to
 * `tw_start_at`, so this follows the same order. `releasedAt`/`createdAt` are
 * last-resort guesses; null lets the sheet open on its own default, today.
 */
const resolveShiftDate = (shift: any): Date | null =>
  toDate(shift?.startedAt) ??
  toDate(shift?.twStartAt) ??
  toDate(shift?.releasedAt) ??
  toDate(shift?.createdAt);

interface UseShiftApprovalProps {
  selectedCompanyId: number | string | null | undefined;
  getShifts: () => ApprovalShiftData[];
  refetch: () => Promise<any> | void;
}

export const useShiftApproval = ({
  selectedCompanyId,
  getShifts,
  refetch,
}: UseShiftApprovalProps) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const { openModal, closeModal } = useModal();
  const openForceCompleteShift = useForceCompleteShiftModal();
  const navigate = useNavigate();
  const companyBase = useCompanyBase();

  const [completeDriverOnShift] = useCompleteDriverOnShiftMutation();
  const [cancelDriverOnShift] = useCancelDriverOnShiftMutation();
  const [approveDriverOnShift] = useApproveDriverOnShiftMutation();
  const [checkDriverOnShiftExists] = useLazyDriverOnShiftExistsQuery();
  const [fetchShiftById] = useLazyGetDriverOnShiftByIdQuery();

  /**
   * Busy for a whole flow, not one mutation: a flow spans several awaits and a
   * confirm modal, and `isLoading` goes false between them.
   *
   * Keyed by shift id because one hook instance serves every card and row — a
   * plain flag would disable the screen. The ref rejects re-entry
   * synchronously, the state drives the buttons, and `lockAlso` claims the
   * other shifts a flow completes on its way.
   */
  const [busyShifts, setBusyShifts] = useState<Record<string, BusyKind>>({});
  const busyRef = useRef(new Set<string>());

  const runExclusive = useCallback(
    async (
      kind: BusyKind,
      shiftId: string,
      run: (lockAlso: (id: ShiftId, as: BusyKind) => boolean) => Promise<void>,
    ) => {
      if (busyRef.current.has(shiftId)) return;
      const held = new Set([shiftId]);
      busyRef.current.add(shiftId);
      setBusyShifts((prev) => ({ ...prev, [shiftId]: kind }));

      /**
       * Claims another shift for the rest of this flow. True means this flow now
       * holds it (or already did). False means a different flow holds it — the
       * caller must not act on that shift: its own flow is mid-request there,
       * and releasing its lock in our `finally` would unblock it early.
       */
      const lockAlso = (id: ShiftId, as: BusyKind) => {
        const key = String(id);
        if (held.has(key)) return true;
        if (busyRef.current.has(key)) return false;
        held.add(key);
        busyRef.current.add(key);
        setBusyShifts((prev) => ({ ...prev, [key]: as }));
        return true;
      };

      try {
        await run(lockAlso);
      } finally {
        held.forEach((key) => busyRef.current.delete(key));
        setBusyShifts((prev) => {
          const next = { ...prev };
          held.forEach((key) => delete next[key]);
          return next;
        });
      }
    },
    [],
  );

  const openApprovalWarningModal = useCallback(
    (
      descriptionKey: string,
      confirmButtonKey: string,
      descriptionParams?: Record<string, string>,
    ) =>
      new Promise<boolean>((resolve) => {
        let modalId = "";
        const onCancel = () => {
          closeModal(modalId);
          resolve(false);
        };
        const onConfirm = () => {
          closeModal(modalId);
          resolve(true);
        };

        modalId = openModal({
          closeButtonTop: "top-4",
          size: "md",
          headerClassName: "!p-4",
          bodyClassName: "!p-4",
          // Dismissing the modal is a "no" — without this the flow would wait
          // on this promise forever, holding the button it disabled.
          onClose: () => resolve(false),
          title: (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="bg-brand-red text-white text-sm font-medium px-2 py-1 rounded-2xl">
                  {t(Namespace.Autopark, "modals.approvalWarning.title")}
                </span>
              </div>
              <p data-testid="start-shift-warning-message" className="text-sm font-normal">
                {t(Namespace.Autopark, descriptionKey as any, descriptionParams)}
              </p>
            </div>
          ),
          body: (
            <div className="flex !px-4 flex-col gap-2">
              <Button
                data-testid="start-shift-warning-confirm"
                className="flex-1 !h-8"
                onClick={onConfirm}
              >
                {t(Namespace.Autopark, confirmButtonKey as any)}
              </Button>
              <Button
                data-testid="start-shift-warning-dismiss"
                className="flex-1 !h-8 !bg-surface-brand !text-white"
                variant="outline"
                onClick={onCancel}
              >
                {t(Namespace.Autopark, "modals.approvalWarning.cancelButton")}
              </Button>
            </div>
          ),
        });
      }),
    [openModal, closeModal, t],
  );

  /**
   * Opens the route sheet on the blocking route: the sheet reads `date`,
   * `mswShiftId` and `mswRouteId` once on mount, loads that day's shifts,
   * selects the shift's vehicle and expands that route's cascade. Get the day
   * wrong and the shift is absent from the list, so the sheet silently falls
   * back to another one — hence the shift is re-read from the server for the
   * fields the list is windowed on. The caller's date stands in if that read
   * fails.
   */
  const openRouteSheet = useCallback(
    async (shiftId: string, routeId: string, shiftDate: Date | null) => {
      let date = shiftDate;
      if (selectedCompanyId) {
        try {
          const shift = await fetchShiftById({
            ownerOrganizationId: BigInt(selectedCompanyId),
            id: BigInt(shiftId),
          }).unwrap();
          date = resolveShiftDate(shift) ?? date;
        } catch (error) {
          console.error("Failed to load shift for route sheet navigation:", error);
        }
      }

      const params = new URLSearchParams({
        status: "sheet",
        mswShiftId: shiftId,
        mswRouteId: routeId,
      });
      if (date) params.set("date", toDateParam(date));
      navigate(`${companyBase}/routes?${params.toString()}`);
    },
    [companyBase, fetchShiftById, navigate, selectedCompanyId],
  );

  const openUnfinishedWorkModal = useCallback(
    (driverName: string, routeId: string, shiftId: string, shiftDate: Date | null = null) =>
      new Promise<boolean>((resolve) => {
        let modalId = "";
        const onCancel = () => {
          closeModal(modalId);
          resolve(false);
        };
        const onConfirm = () => {
          closeModal(modalId);
          resolve(true);
        };
        // Leaving for the sheet ends the flow — the shift stays uncompleted,
        // so this resolves like a cancel rather than a retry.
        const onOpenRouteSheet = () => {
          closeModal(modalId);
          resolve(false);
          void openRouteSheet(shiftId, routeId, shiftDate);
        };
        // The driver cannot finish (accident, breakdown...): the dispatcher ends
        // the shift and settles its work in one go. That modal reports its own
        // outcome, so this flow resolves like a cancel.
        const onForceComplete = () => {
          closeModal(modalId);
          resolve(false);
          openForceCompleteShift(BigInt(shiftId), { onCompleted: () => void refetch() });
        };

        modalId = openModal({
          closeButtonTop: "top-4",
          size: "md",
          headerClassName: "!p-4",
          bodyClassName: "!p-4",
          onClose: () => resolve(false),
          title: (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="bg-brand-red text-white text-sm font-medium px-2 py-1 rounded-2xl">
                  {t(Namespace.Autopark, "modals.unfinishedWork.title")}
                </span>
              </div>
              <p className="text-sm font-normal whitespace-pre-line leading-relaxed">
                {t(Namespace.Autopark, "modals.unfinishedWork.description", {
                  driverName,
                  routeId,
                  shiftId,
                })}
              </p>
            </div>
          ),
          body: (
            <div className="flex !px-4 flex-col gap-2">
              <Button className="flex-1 !h-8" onClick={onConfirm}>
                {t(Namespace.Autopark, "modals.unfinishedWork.retryButton")}
              </Button>
              <Button
                className="flex-1 !h-8"
                variant="opacityBlueWithoutOutline"
                onClick={onOpenRouteSheet}
              >
                {t(Namespace.Autopark, "modals.unfinishedWork.openRouteSheetButton")}
              </Button>
              <Button
                className="flex-1 !h-8"
                variant="opacityBlueWithoutOutline"
                data-testid="unfinished-work-force-complete"
                onClick={onForceComplete}
              >
                {t(Namespace.ForceCompleteShift, "entry")}
              </Button>
              <Button
                className="flex-1 !h-8 !bg-gray-400 hover:!bg-gray-500 !text-white !border-none"
                onClick={onCancel}
              >
                {t(Namespace.Autopark, "modals.unfinishedWork.cancelButton")}
              </Button>
            </div>
          ),
        });
      }),
    [openModal, closeModal, openRouteSheet, openForceCompleteShift, refetch, t],
  );

  const completeShift = useCallback(
    async (shift: { id: ShiftId; driverName?: string; [key: string]: any }) => {
      if (!selectedCompanyId) return;

      const executeCompletion = async (): Promise<void> => {
        try {
          await completeDriverOnShift({
            ownerOrganizationId: BigInt(selectedCompanyId),
            id: BigInt(shift.id),
          }).unwrap();
          await refetch();
        } catch (error) {
          console.error("Failed to complete shift:", error);

          // completeDriverOnShift is off the global error middleware (store.ts),
          // so a toast here is the operator's only feedback.
          const unfinished = unfinishedWork(error);
          if (!unfinished) {
            toast({ title: describeError(error as Partial<ApiError>), variant: "destructive" });
            return;
          }

          const driverName = shift.driverName || t(Namespace.Autopark, "cards.driver");
          // Callers pass as little as `{ id, driverName }`, so the dates the
          // fallback day needs live on the list entry, not on the argument.
          const errorShift =
            getShifts().find((candidate) => String(candidate.id) === unfinished.shiftId) ?? shift;

          const confirmRetry = await openUnfinishedWorkModal(
            driverName,
            unfinished.routeId,
            unfinished.shiftId,
            resolveShiftDate(errorShift),
          );
          if (confirmRetry) {
            await executeCompletion();
          }
        }
      };

      await runExclusive("complete", String(shift.id), executeCompletion);
    },
    [
      selectedCompanyId,
      getShifts,
      refetch,
      completeDriverOnShift,
      openUnfinishedWorkModal,
      runExclusive,
      t,
      toast,
      describeError,
    ],
  );

  /**
   * Calls a shift off, as opposed to `completeShift`, which closes one that ran.
   * The backend decides what is cancellable and rejects the rest.
   */
  const cancelShift = useCallback(
    async (shiftId: ShiftId) => {
      if (!selectedCompanyId) return;

      await runExclusive("cancel", String(shiftId), async () => {
        try {
          await cancelDriverOnShift({
            ownerOrganizationId: BigInt(selectedCompanyId),
            id: BigInt(shiftId),
          }).unwrap();
          await refetch();
        } catch (error) {
          console.error("Failed to cancel shift:", error);
          // The one rejection a dispatcher can act on gets a translated message.
          toast({
            title: t(Namespace.Autopark, "modals.approvalWarning.title"),
            description: unfinishedWork(error)
              ? t(Namespace.Autopark, "errors.cancelActiveRoute")
              : describeError(error as Partial<ApiError>),
            variant: "destructive",
          });
        }
      });
    },
    [selectedCompanyId, refetch, cancelDriverOnShift, runExclusive, toast, t, describeError],
  );

  const approveShift = useCallback(
    async (
      shiftOrId:
        | ShiftId
        | {
            id: ShiftId;
            driverId?: number | bigint;
            truckId?: number | bigint;
            [key: string]: any;
          },
    ) => {
      if (!selectedCompanyId) return;

      const executeApproval = async (
        currentShiftOrId: any,
        lockAlso: (id: ShiftId, as: BusyKind) => boolean,
      ): Promise<void> => {
        /** Which call is in flight — the catch below toasts only for completions. */
        let completing = false;
        /** A conflicting shift another flow holds must not be completed twice. */
        const claimForCompletion = (conflictId: ShiftId) => {
          if (lockAlso(conflictId, "complete")) return true;
          toast({
            title: t(Namespace.Autopark, "errors.shiftActionInProgress" as any),
            variant: "destructive",
          });
          return false;
        };

        let currentShift: any;
        if (
          typeof currentShiftOrId === "number" ||
          typeof currentShiftOrId === "bigint" ||
          typeof currentShiftOrId === "string"
        ) {
          const shiftsList = getShifts();
          currentShift = shiftsList.find((s) => String(s.id) === String(currentShiftOrId));
        } else {
          currentShift = currentShiftOrId;
        }

        if (!currentShift) {
          console.error("Shift not found for approval:", currentShiftOrId);
          return;
        }

        // Kept as the proto value — compared with String, sent with BigInt. Number
        // would round an int64 past 2^53 and match the wrong shift.
        const id = currentShift.id;
        const plannedTruckId = currentShift.truckId;
        const plannedDriverId = currentShift.driverId;

        // Check driver assignment
        if (!plannedDriverId) {
          toast({
            title: t(Namespace.Autopark, "modals.approvalWarning.title") || "Ошибка",
            description: t(Namespace.Autopark, "errors.noDriver" as any),
            variant: "destructive",
          });
          return;
        }

        let truckShiftToComplete: any = undefined;
        let driverShiftToComplete: any = undefined;

        const shiftsList = getShifts();

        try {
          truckShiftToComplete = shiftsList.find(
            (s) =>
              String(s.id) !== String(id) &&
              isShiftRunning(s.status) &&
              s.truckId === plannedTruckId,
          );

          driverShiftToComplete = shiftsList.find(
            (s) =>
              String(s.id) !== String(id) &&
              isShiftRunning(s.status) &&
              Boolean(plannedDriverId) &&
              s.driverId === plannedDriverId,
          );

          if (plannedTruckId && plannedDriverId) {
            try {
              const existsResponse = await checkDriverOnShiftExists({
                truckId: plannedTruckId,
                driverId: plannedDriverId,
                ownerOrganizationIds: [BigInt(selectedCompanyId)],
              }).unwrap();

              const existsShifts = existsResponse?.driverOnShifts || [];
              if (existsShifts.length > 0) {
                const existsTruckShift = existsShifts.find(
                  (s: any) =>
                    s.id !== 0n && s.truck?.id === plannedTruckId && isShiftRunning(s.status),
                );
                const existsDriverShift = existsShifts.find(
                  (s: any) =>
                    s.id !== 0n && s.driver?.id === plannedDriverId && isShiftRunning(s.status),
                );

                if (existsTruckShift && !truckShiftToComplete) {
                  truckShiftToComplete = existsTruckShift;
                }
                if (existsDriverShift) {
                  driverShiftToComplete = existsDriverShift;
                }
              }
            } catch (existsError) {
              console.error("Failed to check DriverOnShiftExists:", existsError);
            }
          }

          if (truckShiftToComplete?.id) {
            const confirmTruckShift = await openApprovalWarningModal(
              "modals.approvalWarning.truck.description",
              "modals.approvalWarning.truck.confirmButton",
            );

            if (!confirmTruckShift) {
              return;
            }

            if (!claimForCompletion(truckShiftToComplete.id)) return;

            completing = true;
            await completeDriverOnShift({
              ownerOrganizationId: BigInt(selectedCompanyId),
              id: BigInt(truckShiftToComplete.id),
            }).unwrap();
            completing = false;
          }

          // Compared as strings: the two candidates can come from different
          // sources — getShifts (a number on the shifts tab) and
          // DriverOnShiftExists (a proto bigint) — so `!==` would read one
          // shift as two and complete it twice. `lockAlso` does not catch that,
          // since the second claim looks like a lock this flow already holds.
          if (
            driverShiftToComplete?.id &&
            (!truckShiftToComplete?.id ||
              String(driverShiftToComplete.id) !== String(truckShiftToComplete.id))
          ) {
            const driverName =
              driverShiftToComplete.driverName ||
              [driverShiftToComplete.driver?.firstName, driverShiftToComplete.driver?.lastName]
                .filter(Boolean)
                .join(" ") ||
              t(Namespace.Autopark, "cards.driver");

            const plateNumber =
              driverShiftToComplete.plateNumber || driverShiftToComplete.truck?.plateNumber || "-";

            const shiftDate =
              (driverShiftToComplete.releasedAt
                ? new Date(driverShiftToComplete.releasedAt).toLocaleDateString()
                : fromTimestamp(driverShiftToComplete.createdAt)?.toLocaleDateString()) || "-";

            const confirmDriverShift = await openApprovalWarningModal(
              "modals.approvalWarning.driver.description",
              "modals.approvalWarning.driver.confirmButton",
              {
                driverName: String(driverName),
                date: String(shiftDate),
                plate: String(plateNumber),
              },
            );

            if (!confirmDriverShift) {
              return;
            }

            if (!claimForCompletion(driverShiftToComplete.id)) return;

            completing = true;
            await completeDriverOnShift({
              ownerOrganizationId: BigInt(selectedCompanyId),
              id: BigInt(driverShiftToComplete.id),
            }).unwrap();
            completing = false;
          }

          await approveDriverOnShift({
            ownerOrganizationId: BigInt(selectedCompanyId),
            id: BigInt(id),
          }).unwrap();
          await refetch();
        } catch (error) {
          console.error("Failed to approve driver change:", error);

          const unfinished = unfinishedWork(error);
          // Only the completions are off the global error middleware; toasting an
          // approve failure here would double the one the middleware already shows.
          if (!unfinished && completing) {
            toast({ title: describeError(error as Partial<ApiError>), variant: "destructive" });
            return;
          }
          if (unfinished) {
            const { shiftId: errorShiftId, routeId } = unfinished;

            // The shift the backend refused to complete — one of the two this
            // flow completed on its way, or, failing that, the row it came from.
            const errorShift =
              [truckShiftToComplete, driverShiftToComplete].find(
                (candidate) => candidate && String(candidate.id) === errorShiftId,
              ) ?? shiftsList.find((s) => String(s.id) === errorShiftId);

            const getDriverNameForShift = () => {
              const driver = errorShift?.driver;
              if (driver) return [driver.firstName, driver.lastName].filter(Boolean).join(" ");
              if (errorShift?.driverName) return errorShift.driverName;
              return t(Namespace.Autopark, "cards.driver");
            };

            const driverName = getDriverNameForShift();
            const confirmRetry = await openUnfinishedWorkModal(
              driverName,
              routeId,
              errorShiftId,
              resolveShiftDate(errorShift),
            );

            if (confirmRetry) {
              await executeApproval(currentShift, lockAlso);
            }
          }
        }
      };

      const shiftId =
        typeof shiftOrId === "object" && shiftOrId !== null ? shiftOrId.id : shiftOrId;
      await runExclusive("approve", String(shiftId), (lockAlso) =>
        executeApproval(shiftOrId, lockAlso),
      );
    },
    [
      selectedCompanyId,
      getShifts,
      refetch,
      completeDriverOnShift,
      approveDriverOnShift,
      checkDriverOnShiftExists,
      openApprovalWarningModal,
      openUnfinishedWorkModal,
      runExclusive,
      toast,
      t,
      describeError,
    ],
  );

  const isCompletingShift = useCallback(
    (shiftId: ShiftId) => busyShifts[String(shiftId)] === "complete",
    [busyShifts],
  );

  /**
   * Any action on this shift is in flight. This is what enables a button — a
   * planned shift offers start and cancel at once and they share the lock, so
   * watching only one of them leaves the other lit and drops the click.
   * `isCompletingShift` is for wording alone.
   */
  const isShiftBusy = useCallback(
    (shiftId: ShiftId) => busyShifts[String(shiftId)] != null,
    [busyShifts],
  );

  return { approveShift, completeShift, cancelShift, isCompletingShift, isShiftBusy };
};
